/**
 * Finans işlemleri için OData yedek (fallback) yolu.
 *
 * Asıl yol `/api/AdminFinans/*` uçlarıdır: tek transaction içinde hem talebi
 * sonuçlandırır hem cüzdanı günceller. O uçlar sunucuya deploy edilene kadar
 * panel çalışmaz durumda kalmasın diye buradaki OData adımları devreye girer.
 *
 * ⚠️ OData yolu transaction DEĞİLDİR: talep güncellenip cüzdan adımı patlarsa
 * çağıran taraf `walletError` alanından haberdar edilir, kullanıcıya gösterilir.
 */

import { API_BASE, getAuthHeaders, apiJson } from "./apiConfig";

/** Uç sunucuda yok mu? (deploy edilmemiş / yanlış route) */
export const isMissingEndpoint = (err) =>
  err && (err.status === 404 || err.status === 405 || err.status === 501);

const odata = (path, options) => apiJson(path, options);

const patchEntity = (entity, id, body) =>
  odata(`/${entity}(${id})`, { method: "PATCH", body: JSON.stringify(body) });

/**
 * Cüzdan hareketi kaydı. Ledger tablosu POST kabul etmezse işlemi
 * çökertmemek için sessizce geçilir — bakiye zaten güncellenmiş olur.
 */
const hareketYaz = async (kullaniciId, islemTipi, miktar, aciklama, siparisId) => {
  try {
    await odata("/KULLANICI_CUZDAN_HAREKETLERI", {
      method: "POST",
      body: JSON.stringify({
        KULLANICI_ID: kullaniciId,
        ISLEM_TIPI: islemTipi,
        MIKTAR: miktar,
        ACIKLAMA: aciklama,
        ISLEM_TARIHI: new Date().toISOString(),
        DURUM: "Tamamlandı",
        ...(siparisId ? { SIPARIS_ID: siparisId } : {}),
      }),
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Kullanıcının cüzdan bakiyesini `miktar` kadar artırır (negatif verilirse azaltır).
 * Cüzdan satırı yoksa oluşturur. Hareket kaydını da yazmaya çalışır.
 */
export async function cuzdanBakiyeEkle(kullaniciId, miktar, aciklama, islemTipi = "IADE", siparisId = null) {
  if (!kullaniciId) throw new Error("Kullanıcı ID bulunamadı, bakiye güncellenemedi.");
  const tutar = Number(miktar) || 0;
  if (!tutar) throw new Error("Geçersiz tutar, bakiye güncellenemedi.");

  const res = await fetch(
    `${API_BASE}/KULLANICI_CUZDAN?$filter=KULLANICI_ID eq ${kullaniciId}&$top=1`,
    { headers: getAuthHeaders() }
  );
  if (!res.ok) throw new Error(`Cüzdan okunamadı (${res.status})`);
  const cuzdan = (await res.json())?.value?.[0];

  if (cuzdan) {
    const yeniBakiye = (Number(cuzdan.BAKIYE) || 0) + tutar;
    await patchEntity("KULLANICI_CUZDAN", cuzdan.ID, {
      BAKIYE: yeniBakiye,
      SON_GUNCELLEME: new Date().toISOString(),
    });
  } else {
    await odata("/KULLANICI_CUZDAN", {
      method: "POST",
      body: JSON.stringify({
        KULLANICI_ID: kullaniciId,
        BAKIYE: tutar,
        SON_GUNCELLEME: new Date().toISOString(),
      }),
    });
  }

  const ledger = await hareketYaz(kullaniciId, islemTipi, tutar, aciklama, siparisId);
  return { ledger };
}

/**
 * Para çekme talebini OData üzerinden sonuçlandırır.
 * @param {object} talep  PARA_CEKME_TALEPLERI satırı
 * @param {"onay"|"red"} action
 * @param {string} aciklama  admin notu (talep tablosunda alan yok → harekete yazılır)
 * @param {boolean} bakiyeIade  red hâlinde tutar cüzdana geri yüklensin mi
 */
export async function paraCekmeFallback(talep, action, aciklama, bakiyeIade = true) {
  const durum = action === "onay" ? "Onaylandı" : "Reddedildi";
  await patchEntity("PARA_CEKME_TALEPLERI", talep.ID, {
    DURUM: durum,
    ISLEM_TARIHI: new Date().toISOString(),
  });

  const not = aciklama ? ` — ${aciklama}` : "";

  if (action === "onay") {
    // Bakiye talep anında düşüldüğü için onayda cüzdana dokunulmaz;
    // sadece izlenebilirlik için hareket kaydı denenir.
    await hareketYaz(
      talep.KULLANICI_ID,
      "PARA_CEKME_ONAY",
      -(Number(talep.MIKTAR) || 0),
      `Para çekme talebi #${talep.ID} onaylandı${not}`
    );
    return { message: "Talep onaylandı." };
  }

  if (!bakiyeIade) {
    await hareketYaz(
      talep.KULLANICI_ID,
      "PARA_CEKME_RED",
      0,
      `Para çekme talebi #${talep.ID} reddedildi (bakiye iadesi yapılmadı)${not}`
    );
    return { message: "Talep reddedildi (bakiye iadesi yapılmadı)." };
  }

  try {
    await cuzdanBakiyeEkle(
      talep.KULLANICI_ID,
      Number(talep.MIKTAR) || 0,
      `Para çekme talebi #${talep.ID} reddedildi${not}`,
      "PARA_CEKME_IADE"
    );
    return { message: "Talep reddedildi, tutar cüzdana iade edildi." };
  } catch (err) {
    return {
      message: "Talep reddedildi.",
      walletError: `Talep reddedildi ancak bakiye iadesi yapılamadı: ${err.message}. Cüzdanı elle düzeltin.`,
    };
  }
}

/**
 * İade talebini OData üzerinden günceller.
 * @param {object} iade  IADE_TALEPLERI satırı
 * @param {string} yeniDurum
 * @param {boolean} bakiyeYukle  onayda tutar alıcı cüzdanına yüklensin mi
 */
export async function iadeFallback(iade, yeniDurum, aciklama, bakiyeYukle = true) {
  const sonuclandi = ["Onaylandı", "Reddedildi"].includes(yeniDurum);
  await patchEntity("IADE_TALEPLERI", iade.ID, {
    DURUM: yeniDurum,
    SONUCLANMA_TARIHI: sonuclandi ? new Date().toISOString() : null,
  });

  const not = aciklama ? ` — ${aciklama}` : "";

  if (yeniDurum !== "Onaylandı" || !bakiyeYukle) {
    // Admin notu IADE_TALEPLERI.ACIKLAMA'ya yazılmaz: orası kullanıcının
    // kendi iade açıklaması, üzerine yazmak veri kaybı olur.
    await hareketYaz(
      iade.KULLANICI_ID,
      "IADE_DURUM",
      0,
      `İade talebi #${iade.ID} → ${yeniDurum}${not}`,
      iade.SIPARIS_DETAY?.SIPARIS_ID || null
    );
    return { message: `İade talebi "${yeniDurum}" olarak güncellendi.` };
  }

  try {
    await cuzdanBakiyeEkle(
      iade.KULLANICI_ID,
      Number(iade.IADE_TUTARI) || 0,
      `İade talebi #${iade.ID} onaylandı${not}`,
      "IADE",
      iade.SIPARIS_DETAY?.SIPARIS_ID || null
    );
    return { message: "İade onaylandı, tutar cüzdana yüklendi." };
  } catch (err) {
    return {
      message: "İade onaylandı.",
      walletError: `İade onaylandı ancak cüzdana yükleme yapılamadı: ${err.message}. Bakiyeyi elle düzeltin.`,
    };
  }
}
